import TagManager from 'react-gtm-module'

import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { ID } from 'src/Types/common'
import { User } from 'src/Types/users'
import { ArtistRolesEnum } from 'src/Constants/enums'

import { EventNameEnum, EventTypeEnum } from '../tagManagerEnums'
import { campaignsPage, pageInfo } from '../pageInfo'
import { artistRegistrationCompleteEvent } from '../RegistrationEvents'

export const toggleActivatedEvent = ({
  platformName,
  campaignNumber,
  campaignType,
  userId,
}: {
  platformName: CampaignPlatformTypeEnum
  campaignNumber?: number
  userId?: ID | null
  campaignType: CampaignTypeEnum
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: 'ga_event',
      event_params: {
        event_name: EventNameEnum.ToggleActivated,
        campaign_platform: platformName,
        campaign_type: campaignType,
        campaign_number: campaignNumber,
      },
      user_properties: {
        user_ID: userId,
        account_type: 'artist',
      },
    },
  })
}

export const createdFirstCampaignEvent = (
  platformName: string,
  campaignInfo: {
    user: {
      id: string
      role: string
      email: string
      phone_number: string | null
    }
    spotify_campaign: {
      id: string
    }
    tiktok_campaign: {
      id: number | string
    }
  },
): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CreatedFirstCampaign,
      event_name: EventNameEnum.CreatedFirstCampaign,
      'campaign-type': CampaignTypeEnum.FIRST,
      'campaign-platform': platformName,
      'campaign-number': '1',
      'campaign-id':
        platformName === CampaignPlatformTypeEnum.SPOTIFY
          ? campaignInfo.spotify_campaign.id
          : campaignInfo.tiktok_campaign.id,
      userID: campaignInfo.user.id,
      pageTitle: pageInfo.CreatedFirstCampaign.pageTitle,
      pagePath: pageInfo.CreatedFirstCampaign.pagePath,
      email: campaignInfo.user.email,
      phone: campaignInfo.user.phone_number,
    },
  })
}

export const sendFirstCampaignCreatedEvents = ({
  userId,
  email,
  phoneNumber,
  platform,
  campaignId,
  trackId,
}: {
  userId: string
  email: string
  phoneNumber?: string | null
  platform: CampaignPlatformTypeEnum.SPOTIFY | CampaignPlatformTypeEnum.TIKTOK
  campaignId: string | number
  trackId: string | number
}): void => {
  const isSpotify = platform === CampaignPlatformTypeEnum.SPOTIFY

  createdFirstCampaignEvent(platform, {
    user: {
      id: userId,
      role: ArtistRolesEnum.ARTIST,
      phone_number: phoneNumber || null,
      email,
    },
    spotify_campaign: { id: isSpotify ? String(campaignId) : '' },
    tiktok_campaign: { id: isSpotify ? '' : campaignId },
  })

  createdCampaignEvent({
    isFirstCampaign: true,
    platformName: platform,
    campaignNumber: 1,
    campaignId,
    trackId,
    userId,
  })

  artistRegistrationCompleteEvent({
    platformName: platform,
    userId,
    userRole: ArtistRolesEnum.ARTIST,
    ...(isSpotify
      ? { spotifyCampaignId: String(campaignId) }
      : { tiktokCampaignId: String(campaignId) }),
  })
}

export const createCampaignEvent = (
  userID: string,
  campaignId: string,
): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CreateCampaign,
      campaignID: campaignId,
      pageTitle: 'Created Campaign',
      pagePath: campaignsPage,
      userID,
    },
  })
}

export const createdCampaignEvent = ({
  isFirstCampaign,
  platformName,
  campaignId,
  trackId,
  userId,
  campaignNumber,
}: {
  isFirstCampaign: boolean
  platformName: string
  campaignId: number | string
  trackId: number | string
  userId: string
  campaignNumber?: number
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CreatedCampaign,
      event_name: EventNameEnum.CreatedCampaign,
      'campaign-type':
        (isFirstCampaign && campaignNumber === 1) || !userId
          ? CampaignTypeEnum.FIRST
          : CampaignTypeEnum.BASIC,
      'campaign-platform': platformName,
      'campaign-number': campaignNumber,
      'campaign-id': campaignId,
      ...(trackId ? { 'track-id': trackId } : {}),
      userID: userId,
    },
  })
}

export const firstCampaignDetailsEvent = ({
  campaignPlatform,
  trackName,
  trackDuration,
  trackId,
  trackArtistName,
}: {
  campaignPlatform: CampaignPlatformTypeEnum
  trackName: string
  trackDuration: string | number
  trackId: string | number
  trackArtistName: string
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.FirstCampaignDetails,
      'campaign-type': CampaignTypeEnum.FIRST,
      'campaign-platform': campaignPlatform,
      'campaign-number': '1',
      'track-name': trackName,
      'track-length': trackDuration,
      'track-id': trackId,
      'track-artist-name': trackArtistName,
    },
  })
}

export const createFirstCampaignStartEvent = (): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.CreateFirstCampaignStart,
    },
  })
}

export const createCampaignStartEvent = (
  user: User,
  campaignNumber: number,
): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.NcStart,
      'campaign-type': user.is_first_campaign
        ? CampaignTypeEnum.FIRST
        : CampaignTypeEnum.BASIC,
      'campaign-number': campaignNumber || 1,
      userID: user.id,
      'User-first-name': user.first_name,
      'registration-date': user.createdAt,
      'account-type': 'musician',
      'user-role': user.role,
    },
  })
}

export const relaunchCampaignListClickEvent = ({
  userId,
  campaignNumber,
  campaignId,
}: {
  userId?: ID | null
  campaignNumber?: number
  campaignId: string | number
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,

      event_params: {
        event_name: EventNameEnum.RelaunchCampaignListClick,
        'campaign-type': CampaignTypeEnum.RELAUNCHED,
        'campaign-platform': CampaignPlatformTypeEnum.SPOTIFY,
        campaign_number: campaignNumber || 1,
        'campaign-id': campaignId,
      },
      user_properties: {
        ...(userId ? { user_id: userId } : {}),
      },
    },
  })
}

export const relaunchCampaignInDetailsClickEvent = ({
  userId,
  campaignNumber,
  campaignId,
}: {
  userId?: ID | null
  campaignNumber?: number
  campaignId: string | number
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,

      event_params: {
        event_name: EventNameEnum.RelaunchDetailsClick,
        'campaign-type': CampaignTypeEnum.RELAUNCHED,
        'campaign-platform': CampaignPlatformTypeEnum.SPOTIFY,
        campaign_number: campaignNumber || 1,
        'campaign-id': campaignId,
      },
      user_properties: {
        ...(userId ? { user_id: userId } : {}),
      },
    },
  })
}

export const newReleasedPromoteEvent = ({
  userId,
  trackId,
}: {
  userId?: ID | null
  trackId?: ID | null
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.NewReleasedPromote,
        track_id: trackId,
      },
      user_properties: {
        ...(userId ? { user_id: userId } : {}),
      },
    },
  })
}

export const newReleasedCampaignsListEvent = ({
  userId,
  trackId,
}: {
  userId?: ID | null
  trackId?: ID | null
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.NewReleasedCampaignsList,
        track_id: trackId,
      },
      user_properties: {
        ...(userId ? { user_id: userId } : {}),
      },
    },
  })
}

export const campaignDetailsViewedEvent = ({
  userId,
  campaignId,
  platformName,
  campaignNumber,
  campaignType,
  amountPaid,
  campaignStartDate,
  campaignEndDate,
  reviews,
  placements,
  campaignStatus,
}: {
  userId?: ID | null
  campaignId?: ID | null
  platformName: CampaignPlatformTypeEnum
  campaignNumber?: number
  campaignType: CampaignTypeEnum
  amountPaid?: number
  campaignStartDate?: string | null
  campaignEndDate?: string | null
  campaignStatus?: string
  reviews?: number
  placements?: number
}): void => {
  TagManager.dataLayer({
    dataLayer: {
      event: EventTypeEnum.GA_EVENT,
      event_params: {
        event_name: EventNameEnum.CampaignDetailsViewed,
        campaign_type: campaignType,
        campaign_platform: platformName,
        campaign_number: campaignNumber,
        campaign_id: campaignId,
        amount_paid: amountPaid,
        campaign_start_date: campaignStartDate,
        campaign_end_date: campaignEndDate,
        campaign_status: campaignStatus,
        // eslint-disable-next-line no-undefined
        ...(reviews !== null && reviews !== undefined ? { reviews } : {}),
        // eslint-disable-next-line no-undefined
        ...(placements !== null && placements !== undefined
          ? { placements }
          : {}),
      },
      user_properties: {
        ...(userId ? { userID: userId } : {}),
      },
    },
  })
}
